import { useEffect, useState } from 'react';
import {
  CONFLICT_WINNERS,
  OVERWATCH_HOURS,
  SYNC_RULES,
  WIZARD_STEPS,
  groupDefaultProblems,
  mappedCount,
  providerHints,
  type CatalogueEntry,
  type ExtensionId,
  type IntegrationInstance,
  type FieldStep,
  type SaveFieldMapsRequest,
  type SaveIntegrationRequest,
  type SyncRules,
  type UserIdentity,
  type WizardStep,
  type ProbeResult,
} from '@forecastify/shared';
import {
  Button,
  Dialog,
  Icon,
  Select,
  Switch,
  TextInput,
  Textarea,
  Tooltip,
} from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import type { RemotePriorityChoice, RemoteStatusChoice } from '@forecastify/shared';
import { DateField } from '../date-field';
import { FieldsStep, type FieldChoice } from './fields-step';
import { PeopleStep } from './people-step';
import { ruleDesc, ruleLabel } from './integration-bits';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C33 · The six-step wizard. `2fazis.markup.html` 3966–4170.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE DIALOG, TWO MODES — and the second is why the steps have names.** *Full* walks all six
 * with a step rail; *single* opens one step from the settings tab's Edit button and saves just that.
 * The mockup addresses a single step by INDEX (`igWizOpen(provider, id, 2)`); ours addresses it by
 * name, because an index silently means a different step the day one is inserted.
 *
 * ⚠️⚠️ **THE DRAFT NEVER HOLDS THE STORED TOKEN, BECAUSE THE SERVER NEVER SENDS IT.** Reopening an
 * existing instance leaves the token field empty with *"the stored one stays until you type a new
 * one"* beside it, and the save omits `authSecret` entirely — which `integration.service.ts` reads
 * as "keep what is stored". A wizard that round-tripped the credential would put it in the browser's
 * memory, in the network tab, and in anybody's HAR file.
 *
 * ⚠️ **Validation is here AND on the server.** `saveIntegrationRequestSchema` re-checks the
 * connection step; this copy exists so somebody finds out on step 1 rather than on step 6.
 */

const STEP_LABEL: Record<WizardStep, MessageKey> = {
  connection: 'integrations.step.connection',
  tasks: 'integrations.step.tasks',
  statuses: 'integrations.step.statuses',
  priorities: 'integrations.step.priorities',
  people: 'integrations.step.people',
  fields: 'integrations.step.fields',
  rules: 'integrations.step.rules',
  review: 'integrations.step.review',
};

export interface Draft {
  name: string;
  baseUrl: string;
  authKind: 'token' | 'oauth';
  authEmail: string;
  /** ⚠️ Only ever what the user just typed. Empty means "leave the stored one alone". */
  authSecret: string;
  query: string;
  sinceOn: string;
  conflictWinner: 'remote' | 'local';
  /** ⚠️ A wall-clock `HH:MM` in the workspace's timezone (P2-24), not an interval and not an instant. */
  overwatchAt: string;
  rules: SyncRules;
  statuses: { remoteStatus: string; taskStatusId: string | null; isDefault: boolean }[];
  /** J-5 · the status map's twin. */
  priorities: { remotePriority: string; taskPriorityId: string | null; isDefault: boolean }[];
}

export function blankDraft(entry: CatalogueEntry, today: string): Draft {
  const hints = providerHints(entry.id);
  return {
    name: '',
    baseUrl: '',
    authKind: 'token',
    authEmail: '',
    authSecret: '',
    query: '',
    sinceOn: today,
    conflictWinner: 'remote',
    overwatchAt: '03:00',
    rules: {
      taskOut: false,
      statusBoth: true,
    },
    /* ⚠️ The provider's usual statuses, unmapped. Nothing has talked to the remote system, so this
       is a starting point somebody edits — not a claim about what is over there. */
    statuses: (hints?.defaultStatuses ?? []).map((remoteStatus) => ({
      remoteStatus,
      taskStatusId: null,
      isDefault: false,
    })),
    priorities: (hints?.defaultPriorities ?? []).map((remotePriority) => ({
      remotePriority,
      taskPriorityId: null,
      isDefault: false,
    })),
  };
}

export function draftOf(instance: IntegrationInstance): Draft {
  return {
    name: instance.name,
    baseUrl: instance.baseUrl,
    authKind: instance.authKind,
    authEmail: instance.authEmail ?? '',
    /* ⚠️ Empty, always. The header says why. */
    authSecret: '',
    query: instance.query,
    sinceOn: instance.sinceOn,
    conflictWinner: instance.conflictWinner,
    overwatchAt: instance.overwatchAt,
    rules: instance.rules,
    statuses: instance.statuses.map((entry) => ({
      remoteStatus: entry.remoteStatus,
      taskStatusId: entry.taskStatusId,
      isDefault: entry.isDefault,
    })),
    priorities: instance.priorities.map((entry) => ({
      remotePriority: entry.remotePriority,
      taskPriorityId: entry.taskPriorityId,
      isDefault: entry.isDefault,
    })),
  };
}

/**
 * The connection step's own refusals — the mockup's `igStepError`.
 *
 * ⚠️ **`hasStoredSecret` is what makes editing possible.** On a new instance an empty token field is
 * an error; on an existing one it means "unchanged", and demanding a re-type on every rename would
 * train people to paste credentials they did not need to.
 */
export function connectionError(
  draft: Draft,
  extensionId: ExtensionId,
  hasStoredSecret: boolean,
): MessageKey | null {
  if (draft.name.trim() === '') return 'integrations.error.name';
  if (!/^https?:\/\/[^\s/]+\.[^\s/]+/u.test(draft.baseUrl.trim())) return 'integrations.error.url';
  if (draft.authKind !== 'token') return null;
  if (providerHints(extensionId)?.needsEmail === true && draft.authEmail.trim() === '') {
    return 'integrations.error.email';
  }
  if (draft.authSecret.trim() === '' && !hasStoredSecret) return 'integrations.error.token';
  return null;
}

/**
 * The statuses step's own refusal — the client-side twin of `saveIntegrationRequestSchema`'s
 * `status_group_needs_default`.
 *
 * ⚠️⚠️ **THE SERVER HAS ENFORCED THIS SINCE C34 AND THE WIZARD NEVER MENTIONED IT.** Map seven Jira
 * statuses onto five of ours and two groups have more than one member; P2-22 says a group of two
 * with no default has an *undefined outbound write*, so the request is refused — after the wizard
 * has said **7 / 7 mapped** and let somebody reach Review. Rule 6 is that the server check is the
 * security; it is not an excuse for the form to stay silent about a rule it can see being broken.
 *
 * ⚠️ Returns the offending group, because *"one of your groups needs a default"* across seven rows
 * is a hunt. A group of exactly one needs no flag — it is its own default.
 */
export function statusesError(draft: Draft): { taskStatusId: string } | null {
  const group = firstGroupProblem(
    draft.statuses
      .filter((entry) => entry.remoteStatus.trim() !== '')
      .map((entry) => ({ groupId: entry.taskStatusId, isDefault: entry.isDefault })),
  );
  return group === null ? null : { taskStatusId: group };
}

/** J-5 · the same refusal over the priority map. */
export function prioritiesError(draft: Draft): { taskPriorityId: string } | null {
  const group = firstGroupProblem(
    draft.priorities
      .filter((entry) => entry.remotePriority.trim() !== '')
      .map((entry) => ({ groupId: entry.taskPriorityId, isDefault: entry.isDefault })),
  );
  return group === null ? null : { taskPriorityId: group };
}

/**
 * ⚠️ **The server's own function, not a copy of it** — `groupDefaultProblems` is what the request
 * schema runs, so the wizard cannot wave somebody through into a refusal the form claims not to
 * see. The first offending group is enough: the rows are marked, and the sentence says what to do.
 */
function firstGroupProblem(
  rows: readonly { readonly groupId: string | null; readonly isDefault: boolean }[],
): string | null {
  const problem = groupDefaultProblems(rows)[0];
  if (problem === undefined) return null;
  return problem.slice(problem.indexOf(':') + 1);
}

/**
 * ⚠️⚠️ **MAPPING A SECOND REMOTE STATUS ONTO ONE OF OURS MARKS THE ONE THAT WAS ALREADY THERE.**
 * Without this, every ordinary act of mapping walks into the refusal above: the row that was alone
 * needed no flag, and the moment it has company neither has one.
 *
 * ⚠️ **This is not a guess about intent — it preserves the behaviour that was already in force.**
 * While it was alone it *was* the status we wrote outbound; keeping it as the default changes
 * nothing about what Forecastify sends. Choosing the new arrival would silently change it.
 */
function withGroupDefaults(rows: readonly MapRow[]): readonly MapRow[] {
  const first = new Map<string, number>();
  const counts = new Map<string, number>();
  rows.forEach((row, index) => {
    if (row.oursId === null) return;
    counts.set(row.oursId, (counts.get(row.oursId) ?? 0) + 1);
    if (!first.has(row.oursId)) first.set(row.oursId, index);
  });

  const settled = new Set<string>();
  for (const [oursId, total] of counts) {
    if (total < 2) continue;
    const marked = rows.some((row) => row.oursId === oursId && row.isDefault);
    if (!marked) settled.add(oursId);
  }
  if (settled.size === 0) return rows;

  return rows.map((row, index) =>
    row.oursId !== null && settled.has(row.oursId)
      ? { ...row, isDefault: first.get(row.oursId) === index }
      : row,
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT A SUCCESSFUL TEST IS A PROOF *OF*.                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **A proof is about a credential, not about a form.** Change the address, the account or the token
 * and the thing that was proved no longer exists — so the gate has to bind to those three and
 * nothing else. Binding it to *the wizard was opened* would let somebody prove one site and save
 * another; binding it to every keystroke would clear the proof when the instance is renamed.
 *
 * ⚠️ **An empty `authSecret` is part of the identity, not a hole in it.** On a saved instance it
 * means *the stored one*, which is a specific credential — so a proof survives a rename, and typing
 * a new token clears it, which is exactly right in both directions.
 */
export function credentialKey(draft: Draft): string {
  return JSON.stringify([draft.baseUrl.trim(), draft.authEmail.trim(), draft.authSecret.trim()]);
}

export function toRequest(
  draft: Draft,
  extensionId: ExtensionId,
  version: number | undefined,
): SaveIntegrationRequest {
  return {
    extensionId,
    name: draft.name.trim(),
    baseUrl: draft.baseUrl.trim(),
    authKind: draft.authKind,
    authEmail: draft.authEmail.trim() === '' ? undefined : draft.authEmail.trim(),
    /* ⚠️ **Omitted when empty, never sent as `''`.** The request schema refuses an empty string, so
       a form submitted with the placeholder still in it cannot blank a working credential. */
    authSecret: draft.authSecret.trim() === '' ? undefined : draft.authSecret.trim(),
    query: draft.query,
    sinceOn: draft.sinceOn,
    conflictWinner: draft.conflictWinner,
    overwatchAt: draft.overwatchAt,
    rules: draft.rules,
    statuses: draft.statuses.filter((entry) => entry.remoteStatus.trim() !== ''),
    priorities: draft.priorities.filter((entry) => entry.remotePriority.trim() !== ''),
    version,
  };
}

export function InstanceWizard({
  open,
  entry,
  instance,
  singleStep,
  today,
  statuses,
  remoteStatuses,
  priorities,
  remotePriorities,
  busy,
  serverError,
  fieldStep,
  identities,
  members,
  onPairIdentity,
  onLoadFields,
  onSaveFields,
  onConnect,
  onClose,
  onSave,
  metafields,
  canCreateMetafields,
  onProbe,
}: {
  readonly open: boolean;
  readonly entry: CatalogueEntry;
  /** Null on create. */
  readonly instance: IntegrationInstance | null;
  /** Set to open exactly one step from the settings tab. */
  readonly singleStep: WizardStep | null;
  readonly today: string;
  readonly statuses: readonly { id: string; name: string; color: string }[];
  /** ⚠️ C39 · what the remote system calls its statuses. Null until we have been able to ask. */
  readonly remoteStatuses: readonly RemoteStatusChoice[] | null;
  /** J-5 · the workspace's priorities and what the remote system calls its own, on the same terms. */
  readonly priorities: readonly { id: string; name: string; color: string }[];
  readonly remotePriorities: readonly RemotePriorityChoice[] | null;
  readonly busy: boolean;
  readonly serverError: string | null;
  /**
   * C34 · the `fields` step's data (P2-20). ⚠️ **Null until asked for**, and asking costs a real
   * round trip to the customer's Jira — so the wizard asks when the step is first OPENED rather
   * than when the wizard is, because most passes through it never reach that step.
   */
  readonly fieldStep: FieldStep | null;
  /** ⚠️ Item 67 · this instance's remote accounts, for the people step. */
  readonly identities: readonly UserIdentity[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  readonly onPairIdentity: (identityId: string, memberId: string | null) => void;
  readonly onLoadFields: () => void;
  readonly onSaveFields: (body: SaveFieldMapsRequest) => void;
  readonly onClose: () => void;
  readonly onSave: (request: SaveIntegrationRequest, fields?: SaveFieldMapsRequest) => void;
  /** `0196` · send the administrator to Atlassian to connect the saved instance. */
  readonly onConnect: (instanceId: string) => void;
  /**
   * ⚠️⚠️ **THE WORKSPACE'S OWN TASK METAFIELDS, SO THE FIELDS STEP WORKS BEFORE A SAVE.**
   * `GET /integrations/:id/fields` returns these alongside the remote list, and needs an instance
   * — which is the whole reason that step used to say *save first*. They are workspace data, not
   * instance data, so the route can read them once and hand them over.
   */
  readonly metafields: FieldStep['metafields'];
  /** ⚠️ `settings.manage`, mirroring what the server puts on `FieldStep.canCreate`. */
  readonly canCreateMetafields: boolean;
  /**
   * ⚠️ **Run the connection test.** `null` back means it failed — the route has already put the
   * reason on `serverError`, because it is the half that knows what a 401 from Atlassian means and
   * this component is the half that knows what to do about it.
   */
  readonly onProbe: (
    request: { kind: 'typed'; draft: Draft } | { kind: 'stored'; instanceId: string },
  ) => Promise<ProbeResult | null>;
}) {
  const t = useT();
  const [draft, setDraft] = useState<Draft>(() => blankDraft(entry, today));
  const [step, setStep] = useState<WizardStep>('connection');
  /* ⚠️ Draft state, like `draft` — nothing is committed until Save. */
  const [fieldChoices, setFieldChoices] = useState<Record<string, FieldChoice>>({});
  const [fieldsAsked, setFieldsAsked] = useState(false);

  useEffect(() => {
    if (step !== 'fields' || fieldsAsked) return;
    setFieldsAsked(true);
    onLoadFields();
  }, [step, fieldsAsked, onLoadFields]);

  /**
   * ⚠️⚠️ **SEEDED FROM WHAT IS ALREADY MAPPED, NEVER FROM WHAT COULD BE** (P2-20). A re-opened
   * wizard must not re-propose twenty fields somebody already declined.
   */
  useEffect(() => {
    if (fieldStep === null) return;
    setFieldChoices(
      Object.fromEntries(
        fieldStep.maps.map((map) => [
          map.remoteFieldId,
          { target: { kind: 'existing' as const, metafieldId: map.metafieldId } },
        ]),
      ),
    );
  }, [fieldStep]);
  const [error, setError] = useState<MessageKey | null>(null);
  /**
   * ⚠️⚠️ **THE PROVED CREDENTIAL, AS A KEY RATHER THAN A BOOLEAN.** A flag would stay true after
   * somebody edited the address — proving one Jira and saving another, silently. `null` is *not
   * proved*; anything else is *this exact credential answered*.
   */
  const [proved, setProved] = useState<string | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [probing, setProbing] = useState(false);
  /* ⚠️ WHICH group is missing its default. The sentence alone leaves somebody hunting across seven
     rows for the two that are in the same group — the rows say it, the sentence says what to do. */
  const [groupError, setGroupError] = useState<string | null>(null);

  /* ⚠️ Re-seeded when the dialog OPENS, not on every render — otherwise every keystroke is undone
     by the next paint. The same guard `project-roles-section.tsx` uses. */
  useEffect(() => {
    if (!open) return;
    setDraft(instance === null ? blankDraft(entry, today) : draftOf(instance));
    setStep(singleStep ?? 'connection');
    setError(null);
    setProbe(null);
    /**
     * ⚠️⚠️ **AN EXISTING INSTANCE OPENS ALREADY PROVED, AND THIS IS THE LINE THAT KEEPS THE GATE
     * USABLE.** Its credential was proved when it was saved and has worked since; demanding a fresh
     * round trip to Atlassian before somebody may rename it or map a status would make the gate a
     * worse version of the bug it sits beside — *"it wants me to type the token in"*. Editing any
     * part of the credential clears it, which is where the gate actually bites.
     */
    setProved(instance === null ? null : credentialKey(draftOf(instance)));
  }, [open, instance, entry, today, singleStep]);

  const patch = (next: Partial<Draft>): void => {
    setDraft((current) => ({ ...current, ...next }));
    setError(null);
  };

  /**
   * ⚠️⚠️ **`hasSecret`, NOT `authHint`.** The hint is a display value — the last four characters —
   * and any path that stores a secret without computing one leaves it null while the secret is
   * perfectly real (the dev seed does exactly that). Using it as the proxy made the connection step
   * refuse to save a working instance, demanding a token nobody needed to retype.
   */
  const hasStoredSecret = instance?.hasSecret === true;
  const index = WIZARD_STEPS.indexOf(step);
  const isSingle = singleStep !== null;

  const guard = (): boolean => {
    /* ⚠️ Checked when LEAVING the connection step and again at the end, because the step rail lets
       somebody jump straight to Review without ever passing through step 1. */
    /**
     * ⚠️⚠️ **THE PROOF IS CHECKED BEFORE THE FORMAT, BECAUSE IT IS THE STRONGER STATEMENT.** A
     * well-formed URL and a well-formed token prove nothing at all — `connectionError` only ever
     * said *this looks like an address*. A green test says Atlassian answered, and says how many
     * projects the credential can actually see, which `jira.md` §9.1 calls the silent ceiling on
     * the whole integration.
     */
    if (draft.authKind === 'token' && proved !== credentialKey(draft)) {
      setError('integrations.error.untested');
      if (!isSingle) setStep('connection');
      return false;
    }

    const problem = connectionError(draft, entry.id, hasStoredSecret);
    if (problem !== null) {
      setError(problem);
      if (!isSingle) setStep('connection');
      return false;
    }

    /* ⚠️ And the same again for the statuses step, for the same reason: the rail lets somebody jump
       to Review without passing through it. */
    const group = statusesError(draft);
    if (group !== null) {
      setGroupError(group.taskStatusId);
      setError('integrations.error.statusDefault');
      if (!isSingle) setStep('statuses');
      return false;
    }
    /* J-5 · and the priorities step. One marker serves both: ids are uuids, so a priority's can
       never light up a status row. */
    const priorityGroup = prioritiesError(draft);
    if (priorityGroup !== null) {
      setGroupError(priorityGroup.taskPriorityId);
      setError('integrations.error.priorityDefault');
      if (!isSingle) setStep('priorities');
      return false;
    }
    setGroupError(null);
    return true;
  };

  /**
   * ⚠️⚠️ **TWO ROUTES, BECAUSE THE BROWSER NEVER HOLDS A SAVED TOKEN.** Typed credentials go to
   * `POST /integrations/probe`, which stores nothing and exists for the case where there is no
   * instance yet. An unchanged credential on a saved instance has no token here to send — so it
   * goes to `POST /integrations/:id/probe`, which reads the stored one. Without the second, the
   * button would only work while somebody was retyping a credential they had no reason to retype.
   */
  /**
   * ⚠️⚠️ **THE FIELDS STEP NOW HAS SOMETHING TO SHOW BEFORE THE INSTANCE EXISTS.** The saved
   * step wins when there is one; otherwise *Test connection* has already brought the remote field
   * list back on the proof, and everything else this step needs — our metafields, and whether the
   * reader may create one — is workspace data the route supplies.
   *
   * ⚠️ **`maps: []` is the truth rather than a placeholder.** Nothing can be mapped yet, because a
   * map row points at an instance id; what the person chooses here is held and written the moment
   * the instance is created — see `submit`.
   */
  const effectiveFieldStep: FieldStep | null =
    fieldStep ??
    (probe === null
      ? null
      : {
          discovered: probe.fields,
          discoveredComplete: probe.fieldsComplete,
          maps: [],
          metafields,
          canCreate: canCreateMetafields,
        });

  const test = (): void => {
    void (async () => {
      setProbing(true);
      setError(null);
      const typed = draft.authSecret.trim() !== '';
      const result = await onProbe(
        typed || instance === null
          ? { kind: 'typed', draft }
          : { kind: 'stored', instanceId: instance.id },
      );
      setProbing(false);
      if (result === null) {
        setProbe(null);
        setProved(null);
        return;
      }
      setProbe(result);
      setProved(credentialKey(draft));
    })();
  };

  const submit = (): void => {
    if (!guard()) return;
    /**
     * ⚠️⚠️ **A CHOICE MADE BEFORE THE INSTANCE EXISTS TRAVELS WITH THE SAVE, OR IT IS LOST.**
     * `onSaveFields` PUTs to `/integrations/:id/fields` and there is no id yet — so on a new
     * connection the field choices went nowhere and the step reported success. Handing them to the
     * route lets it write them straight after the create, where it already fires discovery.
     */
    if (instance === null) {
      onSave(toRequest(draft, entry.id, undefined), pendingFieldMaps() ?? undefined);
      return;
    }
    if (step === 'fields') commitFields();
    onSave(toRequest(draft, entry.id, instance.version));
  };

  /**
   * ⚠️⚠️ **THE FIELD MAP IS SAVED WHEN THE STEP IS LEFT, NOT WITH THE INSTANCE.** Its rows point at
   * `instance_id`, so on a fresh connection there is nothing to attach them to until the instance
   * exists — which is also why the step itself says *finish the connection first*. Sending them
   * with the instance save would need the server to write two tables in one request and would put
   * a Jira round trip inside a form submit.
   */
  const pendingFieldMaps = (): SaveFieldMapsRequest | null => {
    const source = effectiveFieldStep;
    if (source === null) return null;
    return {
      maps: Object.entries(fieldChoices)
        .filter(([, choice]) => choice.target !== null)
        .map(([remoteFieldId, choice]) => {
          const field = source.discovered.find((one) => one.remoteFieldId === remoteFieldId);
          const target = choice.target;
          return {
            remoteFieldId,
            remoteFieldName: field?.name ?? remoteFieldId,
            remoteType: field?.remoteType ?? 'unknown',
            target:
              target?.kind === 'create'
                ? {
                    kind: 'create' as const,
                    name: target.name,
                    type: target.type,
                    /* ⚠️ A choice field's options come from the remote system, not from a human. */
                    options: field?.options ?? [],
                  }
                : { kind: 'existing' as const, metafieldId: target?.metafieldId ?? '' },
          };
        }),
    };
  };

  /** ⚠️ The saved-instance route: the map has somewhere to go right now. */
  const commitFields = (): void => {
    const body = pendingFieldMaps();
    if (body !== null && instance !== null) onSaveFields(body);
  };

  const next = (): void => {
    if (step === 'connection' && !guard()) return;
    if (step === 'fields') commitFields();
    if (index >= WIZARD_STEPS.length - 1) {
      submit();
      return;
    }
    setStep(WIZARD_STEPS[index + 1] as WizardStep);
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      title={
        isSingle
          ? t(STEP_LABEL[step])
          : t(instance === null ? 'integrations.wizard.newTitle' : 'integrations.wizard.editTitle')
      }
      description={t(isSingle ? 'integrations.wizard.singleDesc' : 'integrations.wizard.desc')}
      closeLabel={t('common.close')}
      width={780}
    >
      <div className="flex gap-4">
        {isSingle ? null : (
          <nav
            aria-label={t('integrations.wizard.steps')}
            className="hidden w-[170px] shrink-0 flex-col gap-0.5 sm:flex"
          >
            {WIZARD_STEPS.map((candidate, position) => (
              <button
                key={candidate}
                type="button"
                aria-current={candidate === step ? 'step' : undefined}
                onClick={() => {
                  if (step === 'fields') commitFields();
                  setStep(candidate);
                  setError(null);
                }}
                className={[
                  'hover:bg-accent flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] transition-colors',
                  candidate === step
                    ? 'bg-secondary text-secondary-foreground font-medium'
                    : position < index
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                ].join(' ')}
              >
                <span
                  className={[
                    'grid size-5 shrink-0 place-items-center rounded-full border text-[10.5px] font-medium tabular-nums',
                    position < index
                      ? 'bg-primary text-primary-foreground border-primary'
                      : candidate === step
                        ? 'border-primary text-primary'
                        : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {position + 1}
                </span>
                {t(STEP_LABEL[candidate])}
              </button>
            ))}
          </nav>
        )}

        <div className="flex max-h-[52vh] min-w-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
          {step === 'connection' ? (
            <ConnectionStep
              t={t}
              entry={entry}
              draft={draft}
              instance={instance}
              hasStoredSecret={hasStoredSecret}
              hint={instance?.authHint ?? null}
              patch={patch}
              onTest={test}
              onConnect={() => {
                if (instance !== null) onConnect(instance.id);
              }}
              probing={probing}
              probe={probe}
            />
          ) : null}

          {step === 'tasks' ? <TasksStep t={t} entry={entry} draft={draft} patch={patch} /> : null}

          {step === 'statuses' ? (
            <StatusesStep
              t={t}
              draft={draft}
              statuses={statuses}
              /**
               * ⚠️⚠️ **THE TEST'S STATUSES WIN, AND ON A NEW CONNECTION THEY ARE THE ONLY ONES.**
               * `remoteStatuses` comes from `GET /integrations/:id/statuses`, which needs a SAVED
               * instance — so before the connection test carried them, this step on a new instance
               * had nothing to offer and somebody had to finish the wizard, reopen it and come
               * back. They also win on a saved instance, because a test just ran and a cached list
               * from an earlier session may predate a status somebody added this morning.
               */
              remote={probe?.statuses ?? remoteStatuses}
              patch={patch}
              groupError={groupError}
            />
          ) : null}

          {step === 'priorities' ? (
            <PrioritiesStep
              t={t}
              draft={draft}
              priorities={priorities}
              /* ⚠️ The test's list wins here too, for the statuses' reason. */
              remote={probe?.priorities ?? remotePriorities}
              patch={patch}
              groupError={groupError}
            />
          ) : null}

          {step === 'people' ? (
            <PeopleStep
              t={t}
              identities={identities}
              members={members}
              /* ⚠️ An identity row points at an instance id, so there is nothing to pair until
                 one exists. Unlike the fields step, a connection TEST does not stand in: pairing
                 writes immediately rather than travelling in the draft. */
              needsSave={instance === null}
              /* ⚠️ The connection test's roster, which is all this step can honestly show before
                 the instance exists. Empty on a saved one, where the branch is unreachable. */
              preview={probe?.people ?? []}
              busy={busy}
              onPair={onPairIdentity}
            />
          ) : null}

          {step === 'fields' ? (
            <FieldsStep
              t={t}
              /**
               * ⚠️⚠️ **`instance === null && probe === null`, WHERE THIS WAS
               * `effectiveFieldStep === null` - item 67.** Those are not the same question, and
               * the difference was a lie on screen: on a SAVED instance the field step arrives
               * from `GET /integrations/:id/fields`, which is a round trip, and for the whole of
               * it `effectiveFieldStep` is null - so the step said *"save the connection first"*
               * to somebody looking at a connection they had already saved. Its own loading branch
               * was unreachable.
               *
               * ⚠️ **A probe counts.** Pressing *Test connection* fills this step on an unsaved
               * instance, so *"save it first"* is only true when there is neither.
               */
              needsSave={instance === null && probe === null}
              step={effectiveFieldStep}
              choices={fieldChoices}
              onChange={(remoteFieldId, choice) => {
                setFieldChoices((current) => ({ ...current, [remoteFieldId]: choice }));
              }}
            />
          ) : null}

          {step === 'rules' ? <RulesStep t={t} draft={draft} patch={patch} /> : null}

          {step === 'review' ? <ReviewStep t={t} draft={draft} /> : null}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 border-t pt-3">
        <span className="text-destructive min-w-0 flex-1 text-[11.5px] leading-relaxed">
          {error === null ? (serverError ?? '') : t(error)}
        </span>
        <div className="flex gap-2">
          {isSingle ? (
            <Button variant="outline" size="lg" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="lg"
              disabled={index === 0}
              onClick={() => {
                if (step === 'fields') commitFields();
                setStep(WIZARD_STEPS[index - 1] as WizardStep);
                setError(null);
              }}
            >
              {t('integrations.wizard.back')}
            </Button>
          )}
          <Button size="lg" disabled={busy} onClick={isSingle ? submit : next}>
            {isSingle
              ? t('common.save')
              : index === WIZARD_STEPS.length - 1
                ? t(
                    instance === null
                      ? 'integrations.wizard.finish'
                      : 'integrations.wizard.saveEdit',
                  )
                : t('integrations.wizard.next')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  children,
  className,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <label className={['flex flex-col gap-1.5', className ?? ''].join(' ')}>
      <span className="text-[12px] font-medium">{label}</span>
      {children}
      {hint === undefined ? null : (
        <span className="text-muted-foreground text-[11.5px] leading-relaxed">{hint}</span>
      )}
    </label>
  );
}

function ConnectionStep({
  t,
  entry,
  draft,
  instance,
  hasStoredSecret,
  hint,
  patch,
  onTest,
  onConnect,
  probing,
  probe,
}: {
  readonly t: Translate;
  readonly entry: CatalogueEntry;
  readonly draft: Draft;
  /** ⚠️ `0196` · null until saved — and OAuth can only connect a saved instance. */
  readonly instance: IntegrationInstance | null;
  readonly hasStoredSecret: boolean;
  readonly hint: string | null;
  readonly patch: (next: Partial<Draft>) => void;
  /** ⚠️ Runs the test. The step does not know which of the two routes it takes — see `test`. */
  readonly onTest: () => void;
  /** `0196` · the OAuth round trip, for a saved instance. */
  readonly onConnect: () => void;
  readonly probing: boolean;
  /** What the last successful test saw, or `null` if there has not been one on this credential. */
  readonly probe: ProbeResult | null;
}) {
  const hints = providerHints(entry.id);

  return (
    <div className="flex flex-col gap-3">
      <Field label={t('integrations.field.name')}>
        <TextInput
          value={draft.name}
          onChange={(event) => {
            patch({ name: event.target.value });
          }}
          placeholder={t('integrations.field.namePlaceholder')}
        />
      </Field>

      <Field label={t('integrations.field.url')}>
        <TextInput
          value={draft.baseUrl}
          onChange={(event) => {
            patch({ baseUrl: event.target.value });
          }}
          placeholder={hints?.urlExample ?? ''}
          className="text-[12px]"
        />
      </Field>

      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium">{t('integrations.field.auth')}</span>
        <div className="bg-muted inline-flex w-fit gap-0.5 rounded-lg p-0.5">
          {(['token', 'oauth'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              aria-pressed={draft.authKind === kind}
              onClick={() => {
                patch({ authKind: kind });
              }}
              className={[
                'rounded-md px-3 py-1 text-[12px] font-medium transition-colors',
                draft.authKind === kind
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {t(kind === 'token' ? 'integrations.conn.tokenKind' : 'integrations.conn.oauth')}
            </button>
          ))}
        </div>
      </div>

      {draft.authKind === 'token' ? (
        <div className="flex flex-col gap-3 rounded-lg border p-3">
          {hints?.needsEmail === true ? (
            <Field label={t('integrations.field.email')}>
              <TextInput
                type="email"
                value={draft.authEmail}
                onChange={(event) => {
                  patch({ authEmail: event.target.value });
                }}
                placeholder="integration@ceged.hu"
              />
            </Field>
          ) : null}
          <Field
            label={t('integrations.field.token')}
            /* ⚠️ The hint IS the explanation of an empty field on an existing instance. Without it
               somebody assumes the token was lost and pastes a new one for no reason. */
            hint={hasStoredSecret ? t('integrations.field.tokenKept') : undefined}
          >
            <TextInput
              type="password"
              value={draft.authSecret}
              onChange={(event) => {
                patch({ authSecret: event.target.value });
              }}
              placeholder={hint ?? '••••••••••••'}
              className="text-[12px]"
            />
          </Field>

          {/**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ *Prove what you can see* — `jira.md` §9.1, on a button.  │
           * └───────────────────────────────────────────────────────────────┘
           *
           * ⚠️⚠️ **THE LIST MATTERS MORE THAN THE TICK.** A green mark says the token works; the
           * project list says what it can REACH — and the admin credential's visibility is the
           * silent ceiling on the whole integration. A client who expected eleven and sees four
           * has learned in three seconds what otherwise surfaces months later as *"why is that
           * project not syncing"*.
           */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onTest} disabled={probing}>
              {probing ? t('integrations.conn.testing') : t('integrations.conn.test')}
            </Button>
            {probe === null ? (
              <span className="text-muted-foreground text-[11.5px]">
                {t('integrations.conn.testHint')}
              </span>
            ) : (
              <span className="text-[11.5px] font-medium text-emerald-600">
                {t('integrations.conn.testOk', { count: probe.projects.length })}
              </span>
            )}
          </div>

          <ProbeSummary t={t} probe={probe} />
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-lg border p-3">
          <p className="text-muted-foreground text-[12px] leading-relaxed">
            {t('integrations.oauthNote')}
          </p>
          {/**
           * ⚠️ `0196` · the round trip needs a ROW to hang the grant on, so an unsaved instance
           * says *save first* rather than offering a button that could not land anywhere. Once
           * saved, the same step either offers the approval or shows who approved — and the
           * proof below is the same proof a token gets, because the ceiling §9.1 warns about is
           * the account's visibility whichever way it authenticates.
           */}
          {instance === null ? (
            <p className="text-[11.5px]">{t('integrations.oauth.saveFirst')}</p>
          ) : hasStoredSecret ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11.5px] font-medium text-emerald-600">
                {t('integrations.oauth.connected', { account: hint ?? instance.authEmail ?? '' })}
              </span>
              <Button variant="outline" size="sm" onClick={onTest} disabled={probing}>
                {probing ? t('integrations.conn.testing') : t('integrations.conn.test')}
              </Button>
              <Button variant="ghost" size="sm" onClick={onConnect}>
                {t('integrations.oauth.reconnect')}
              </Button>
            </div>
          ) : (
            <div>
              <Button size="sm" onClick={onConnect}>
                {t('integrations.oauth.connect')}
              </Button>
            </div>
          )}
          <ProbeSummary t={t} probe={probe} />
          <p className="text-muted-foreground text-[11.5px]">
            {t('integrations.oauth.webhookNote')}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * ⚠️⚠️ **WHO THE CREDENTIAL IS, BESIDE WHAT IT CAN SEE — AND THE TICK NOW MEANS SOMETHING.**
 * Until 8 Sep 2026 a **revoked** Jira token produced this same green line reading
 * *"Working — 0 projects visible"*, because both calls the proof rested on answer 200 to
 * an anonymous caller. `RemoteReader.whoAmI` refuses that case outright, so zero here now
 * genuinely means *this account can browse nothing* — which is a different afternoon's
 * work from *your token expired*, and the two were previously the same screen.
 *
 * ⚠️ **And a name catches the mistake §9.1 actually warns about.** An Atlassian API token
 * is ACCOUNT-scoped, so the common error is a personal token where a service account was
 * intended — invisible until that person leaves and every sync stops at once. Reading
 * one's own name here is how somebody notices while it still costs nothing. `0196` makes the
 * same point for an OAuth grant: it reads as whoever approved it.
 */
function ProbeSummary({ t, probe }: { readonly t: Translate; readonly probe: ProbeResult | null }) {
  if (probe === null) return null;
  return (
    <>
      <p className="text-muted-foreground text-[11.5px]">
        {t('integrations.conn.testAs', { name: probe.account.name })}
        {probe.account.email === null ? null : <span> · {probe.account.email}</span>}
      </p>
      <div className="max-h-40 overflow-auto rounded-md border">
        {probe.projects.map((project) => (
          <div
            key={project.remoteProjectId}
            className="flex items-center gap-2 border-b px-2.5 py-1.5 text-[12px] last:border-0"
          >
            <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11px]">
              {project.key}
            </span>
            <span className="min-w-0 flex-1 truncate">{project.name}</span>
            {project.archived ? (
              <span className="text-muted-foreground text-[11px]">
                {t('integrations.links.archived')}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {probe.fieldsComplete ? null : (
        /* ⚠️ The field walk stopped at the proof's deadline on a large site; the saved instance's
           Fields step reads the rest. Said here so a short list is not read as a broken one. */
        <p className="text-muted-foreground text-[11.5px]">
          {t('integrations.conn.fieldsPartial')}
        </p>
      )}
    </>
  );
}

function TasksStep({
  t,
  entry,
  draft,
  patch,
}: {
  readonly t: Translate;
  readonly entry: CatalogueEntry;
  readonly draft: Draft;
  readonly patch: (next: Partial<Draft>) => void;
}) {
  const hints = providerHints(entry.id);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 rounded-lg border p-3">
        {(['taskOut'] as const).map((rule) => (
          <RuleSwitch key={rule} t={t} rule={rule} draft={draft} patch={patch} />
        ))}
      </div>

      <Field label={t('integrations.field.query')}>
        <Textarea
          value={draft.query}
          onChange={(event) => {
            patch({ query: event.target.value });
          }}
          rows={2}
          placeholder={hints?.queryExample ?? ''}
          className="text-[12px]"
        />
      </Field>

      <Field
        label={t('integrations.field.since')}
        hint={t('integrations.field.sinceHint')}
        className="w-[190px]"
      >
        {/* ⚠️ `DateField`, never a native date input — the product owner's decision in C25. */}
        <DateField
          value={draft.sinceOn}
          label={t('integrations.field.since')}
          onChange={(value) => {
            patch({ sinceOn: value === '' ? draft.sinceOn : value });
          }}
        />
      </Field>
    </div>
  );
}

const CATEGORY_LABEL: Record<RemoteStatusChoice['category'], MessageKey> = {
  new: 'integrations.statuses.cat.new',
  indeterminate: 'integrations.statuses.cat.doing',
  done: 'integrations.statuses.cat.done',
  unknown: 'integrations.statuses.cat.unknown',
};

/** One row of either map, in the shape the step draws: theirs, ours, and the group's flag. */
interface MapRow {
  readonly remote: string;
  readonly oursId: string | null;
  readonly isDefault: boolean;
}

/** The strings a map step needs, so the statuses and the priorities read as two steps, not one. */
interface MapCopy {
  readonly lede: MessageKey;
  readonly none: MessageKey;
  readonly pick: MessageKey;
  readonly remote: MessageKey;
  readonly empty: MessageKey;
  readonly emptyKnown: MessageKey;
  readonly addAll: MessageKey;
  readonly add: MessageKey;
  readonly note: MessageKey;
  readonly defaultLabel: MessageKey;
  readonly defaultHint: MessageKey;
}

const STATUS_COPY: MapCopy = {
  lede: 'integrations.statuses.lede',
  none: 'integrations.statuses.none',
  pick: 'integrations.statuses.pick',
  remote: 'integrations.statuses.remote',
  empty: 'integrations.statuses.empty',
  emptyKnown: 'integrations.statuses.emptyKnown',
  addAll: 'integrations.statuses.addAll',
  add: 'integrations.statuses.add',
  note: 'integrations.statuses.note',
  defaultLabel: 'integrations.statuses.default',
  defaultHint: 'integrations.statuses.defaultHint',
};

const PRIORITY_COPY: MapCopy = {
  lede: 'integrations.priorities.lede',
  none: 'integrations.priorities.none',
  pick: 'integrations.priorities.pick',
  remote: 'integrations.priorities.remote',
  empty: 'integrations.priorities.empty',
  emptyKnown: 'integrations.priorities.emptyKnown',
  addAll: 'integrations.priorities.addAll',
  add: 'integrations.priorities.add',
  note: 'integrations.priorities.note',
  defaultLabel: 'integrations.priorities.default',
  defaultHint: 'integrations.priorities.defaultHint',
};

/** The statuses step: the generic map step, fed the status rows and the status copy. */
function StatusesStep({
  t,
  draft,
  statuses,
  remote,
  patch,
  groupError,
}: {
  readonly t: Translate;
  readonly draft: Draft;
  readonly statuses: readonly { id: string; name: string; color: string }[];
  /**
   * ⚠️⚠️ **WHAT JIRA ACTUALLY CALLS ITS STATUSES, AND NULL UNTIL WE HAVE ASKED.** Null and empty are
   * different: null is *"we have not been able to look"* — an unsaved instance, or a read that
   * failed — and the row falls back to a text box so nobody is stuck. Empty would be a site with no
   * statuses, which does not happen.
   */
  readonly remote: readonly RemoteStatusChoice[] | null;
  readonly patch: (next: Partial<Draft>) => void;
  readonly groupError: string | null;
}) {
  return (
    <MapStep
      t={t}
      copy={STATUS_COPY}
      rows={draft.statuses.map((entry) => ({
        remote: entry.remoteStatus,
        oursId: entry.taskStatusId,
        isDefault: entry.isDefault,
      }))}
      ours={statuses}
      /* ⚠️ *Done* and *Cancelled* are both terminal; the category is the only thing that tells
         one kind of ending from the other at a glance. */
      remote={
        remote === null
          ? null
          : remote.map((one) => ({
              name: one.name,
              label: `${one.name} · ${t(CATEGORY_LABEL[one.category])}`,
            }))
      }
      groupError={groupError}
      onChange={(rows) => {
        patch({
          statuses: rows.map((row) => ({
            remoteStatus: row.remote,
            taskStatusId: row.oursId,
            isDefault: row.isDefault,
          })),
        });
      }}
    />
  );
}

/**
 * J-5 · the priorities step — the same component with the nouns swapped.
 *
 * ⚠️ **No category suffix**: Jira ranks a priority rather than grading it, and the list arrives in
 * rank order, so the position says what a second column would.
 */
function PrioritiesStep({
  t,
  draft,
  priorities,
  remote,
  patch,
  groupError,
}: {
  readonly t: Translate;
  readonly draft: Draft;
  readonly priorities: readonly { id: string; name: string; color: string }[];
  readonly remote: readonly RemotePriorityChoice[] | null;
  readonly patch: (next: Partial<Draft>) => void;
  readonly groupError: string | null;
}) {
  return (
    <MapStep
      t={t}
      copy={PRIORITY_COPY}
      rows={draft.priorities.map((entry) => ({
        remote: entry.remotePriority,
        oursId: entry.taskPriorityId,
        isDefault: entry.isDefault,
      }))}
      ours={priorities}
      remote={remote === null ? null : remote.map((one) => ({ name: one.name, label: one.name }))}
      groupError={groupError}
      onChange={(rows) => {
        patch({
          priorities: rows.map((row) => ({
            remotePriority: row.remote,
            taskPriorityId: row.oursId,
            isDefault: row.isDefault,
          })),
        });
      }}
    />
  );
}

/**
 * A vocabulary map — theirs on the left, ours on the right, one default per group.
 *
 * ⚠️ **Domain-agnostic on purpose** (CLAUDE.md §3): it knows about rows, a list of ours and a list
 * of theirs, and which strings to say. The statuses step and the priorities step are this with
 * their own nouns, and a third map would be too.
 */
function MapStep({
  t,
  copy,
  rows,
  ours,
  remote,
  groupError,
  onChange,
}: {
  readonly t: Translate;
  readonly copy: MapCopy;
  readonly rows: readonly MapRow[];
  readonly ours: readonly { id: string; name: string; color: string }[];
  /** Null until we have been able to ask — the row falls back to a text box. */
  readonly remote: readonly { name: string; label: string }[] | null;
  /**
   * ⚠️ The Forecastify value whose group has no single default, or null. Marking the rows is the
   * half of the message that says WHERE — the sentence below the buttons says what to do.
   */
  readonly groupError: string | null;
  readonly onChange: (rows: readonly MapRow[]) => void;
}) {
  const unmapped = rows.filter((entry) => entry.oursId === null).length;
  const options = [
    { value: '', label: t(copy.none) },
    ...ours.map((one) => ({ value: one.id, label: one.name })),
  ];

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-[12px] leading-relaxed">{t(copy.lede)}</p>

      <div className="overflow-hidden rounded-lg border">
        {rows.map((entry, position) => (
          <div
            key={position}
            className={`flex items-center gap-3 border-b px-3 py-2 last:border-0 ${
              groupError !== null && entry.oursId === groupError
                ? 'bg-destructive/5 ring-destructive/40 ring-1 ring-inset'
                : ''
            }`}
          >
            {/**
             * ⚠️⚠️ **A PICKER, BECAUSE A TYPED STATUS NAME FAILS SILENTLY.** This was a text box
             * seeded from a hard-coded guess — *To Do, In Progress, In Review, Done* — and a name
             * that does not match theirs exactly is a transition Jira never offers: refused,
             * counted, abandoned after five attempts, with nothing anywhere saying *"you wrote Kész
             * and they call it Done"*. A picker cannot be typed wrong.
             *
             * ⚠️ **The text box survives as the fallback**, for an instance not yet saved or a read
             * that failed. Being unable to reach Jira must not mean being unable to configure.
             */}
            {remote === null ? (
              <TextInput
                value={entry.remote}
                aria-label={t(copy.remote)}
                onChange={(event) => {
                  onChange(
                    rows.map((other, index) =>
                      index === position ? { ...other, remote: event.target.value } : other,
                    ),
                  );
                }}
                className="min-w-0 flex-1"
              />
            ) : (
              <Select
                value={entry.remote}
                aria-label={t(copy.remote)}
                onChange={(event) => {
                  onChange(
                    rows.map((other, index) =>
                      index === position ? { ...other, remote: event.target.value } : other,
                    ),
                  );
                }}
                options={[
                  { value: '', label: t(copy.pick) },
                  /* ⚠️ A value already mapped but no longer on the site keeps its row rather than
                     vanishing from the picker — otherwise saving would silently drop it. */
                  ...(remote.some((one) => one.name === entry.remote) || entry.remote === ''
                    ? []
                    : [{ value: entry.remote, label: entry.remote }]),
                  ...remote.map((one) => ({ value: one.name, label: one.label })),
                ]}
                className="min-w-0 flex-1"
              />
            )}
            <Icon name="arrow-right" size={13} className="text-muted-foreground shrink-0" />
            <span
              className="size-2.5 shrink-0 rounded-full border"
              style={{
                background: ours.find((one) => one.id === entry.oursId)?.color ?? 'transparent',
              }}
              aria-hidden
            />
            <Select
              value={entry.oursId ?? ''}
              options={options}
              aria-label={entry.remote}
              onChange={(event) => {
                /* ⚠️ `withGroupDefaults` is why mapping a second value onto one of ours does not
                   walk straight into the refusal — see its note. */
                onChange(
                  withGroupDefaults(
                    rows.map((other, index) =>
                      index === position
                        ? {
                            ...other,
                            oursId: event.target.value === '' ? null : event.target.value,
                          }
                        : other,
                    ),
                  ),
                );
              }}
              className="w-[190px] shrink-0"
            />
            {/* ⚠️⚠️ **THE DEFAULT, AND IT ONLY MEANS ANYTHING INSIDE A GROUP.** Several remote
                values may point at one of ours; this marks the one we WRITE when the value changes
                here. Hidden while the row is unmapped, because an unmapped row belongs to no group
                and a default there would have nothing to be the default of. */}
            {entry.oursId === null ? (
              /* 5 Nov 2026 · 128px — *Alapértelmezett* overflowed the 92px it had. */
              <span className="w-[128px] shrink-0" aria-hidden />
            ) : (
              <Tooltip content={t(copy.defaultHint)}>
                <Button
                  variant={entry.isDefault ? 'default' : 'outline'}
                  size="sm"
                  aria-pressed={entry.isDefault}
                  onClick={() => {
                    /* ⚠️ Exactly one per group: choosing this one clears every other row pointing at
                     the same Forecastify value — which is what the partial unique index enforces
                     at the far end, and what the request schema refuses without. */
                    onChange(
                      rows.map((other, index) =>
                        other.oursId === entry.oursId
                          ? { ...other, isDefault: index === position }
                          : other,
                      ),
                    );
                  }}
                  className="w-[128px] shrink-0"
                >
                  {t(copy.defaultLabel)}
                </Button>
              </Tooltip>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('common.delete')}
              onClick={() => {
                /* ⚠️ Deleting the DEFAULT row leaves its group with none — the same dead end the
                   mapping change avoids, reached from the other side. */
                onChange(withGroupDefaults(rows.filter((_, index) => index !== position)));
              }}
            >
              <Icon name="trash" size={14} />
            </Button>
          </div>
        ))}
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
            <p className="text-muted-foreground text-[12px]">
              {t(remote === null || remote.length === 0 ? copy.empty : copy.emptyKnown)}
            </p>
            {/**
             * ⚠️⚠️ **THE ANSWER TO *"WHY AM I TYPING THESE?"*.** The step opened empty and asked
             * somebody to add rows one at a time and type a name into each — a name that has to
             * match Jira's exactly or the transition is one Jira never offers, refused five times
             * and then abandoned, silently. We know the list; the only thing left for a person to
             * decide is which of ours each one means.
             *
             * ⚠️ **A button rather than filling the rows on open.** Populating a draft as a side
             * effect of rendering would make *Cancel* ambiguous — and somebody who has deliberately
             * mapped four of seven does not want the other three appearing under them.
             */}
            {remote === null || remote.length === 0 ? null : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onChange(
                    remote.map((one) => ({ remote: one.name, oursId: null, isDefault: false })),
                  );
                }}
              >
                <Icon name="plus" size={13} />
                {t(copy.addAll, { count: remote.length })}
              </Button>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onChange([...rows, { remote: '', oursId: null, isDefault: false }]);
          }}
        >
          <Icon name="plus" size={13} />
          {t(copy.add)}
        </Button>
        <span className="text-muted-foreground text-[11.5px]">
          {t(copy.note, { count: unmapped })}
        </span>
      </div>
    </div>
  );
}

function RuleSwitch({
  t,
  rule,
  draft,
  patch,
}: {
  readonly t: Translate;
  readonly rule: (typeof SYNC_RULES)[number];
  readonly draft: Draft;
  readonly patch: (next: Partial<Draft>) => void;
}) {
  return (
    <div className="flex items-start gap-2.5 text-[12.5px]">
      <Switch
        checked={draft.rules[rule]}
        label={ruleLabel(t, rule)}
        onCheckedChange={(value) => {
          patch({ rules: { ...draft.rules, [rule]: value } });
        }}
      />
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{ruleLabel(t, rule)}</span>
        <span className="text-muted-foreground text-[11.5px] leading-relaxed">
          {ruleDesc(t, rule)}
        </span>
      </span>
    </div>
  );
}

function RulesStep({
  t,
  draft,
  patch,
}: {
  readonly t: Translate;
  readonly draft: Draft;
  readonly patch: (next: Partial<Draft>) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 rounded-lg border p-3">
        {SYNC_RULES.map((rule) => (
          <RuleSwitch key={rule} t={t} rule={rule} draft={draft} patch={patch} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <Field label={t('integrations.field.conflict')} className="min-w-[190px] flex-1">
          <Select
            value={draft.conflictWinner}
            options={CONFLICT_WINNERS.map((winner) => ({
              value: winner,
              label: t(
                winner === 'remote'
                  ? 'integrations.conflict.remote'
                  : 'integrations.conflict.local',
              ),
            }))}
            onChange={(event) => {
              patch({ conflictWinner: event.target.value as 'remote' | 'local' });
            }}
            className="w-full"
          />
        </Field>
        {/* ⚠️⚠️ **A TIME OF DAY, NOT AN INTERVAL** (P2-24). The 5/15/60 picker offered a choice
            that no longer exists: the reconciliation runs once a day and never more often, so the
            only question left is when it runs. */}
        <Field label={t('integrations.field.overwatch')} className="min-w-[190px] flex-1">
          <Select
            value={draft.overwatchAt}
            options={OVERWATCH_HOURS.map((hour) => ({ value: hour, label: hour }))}
            onChange={(event) => {
              patch({ overwatchAt: event.target.value });
            }}
            className="w-full"
          />
        </Field>
      </div>
    </div>
  );
}

function ReviewStep({ t, draft }: { readonly t: Translate; readonly draft: Draft }) {
  const statuses = mappedCount(draft.statuses);
  const priorities = mappedCount(draft.priorities);
  const on = SYNC_RULES.filter((rule) => draft.rules[rule]);

  const rows: readonly { key: string; label: string; value: string }[] = [
    { key: 'name', label: t('integrations.review.name'), value: draft.name || '–' },
    { key: 'url', label: t('integrations.review.url'), value: draft.baseUrl || '–' },
    {
      key: 'auth',
      label: t('integrations.review.auth'),
      value:
        draft.authKind === 'oauth'
          ? t('integrations.conn.oauth')
          : `${t('integrations.conn.tokenKind')} · ${draft.authEmail || '–'}`,
    },
    {
      key: 'query',
      label: t('integrations.review.query'),
      value: draft.query.trim() === '' ? t('integrations.review.queryEmpty') : draft.query,
    },
    {
      key: 'statuses',
      label: t('integrations.review.statuses'),
      value: t('integrations.map.count', statuses),
    },
    {
      key: 'priorities',
      label: t('integrations.review.priorities'),
      value: t('integrations.map.count', priorities),
    },
    {
      key: 'rules',
      label: t('integrations.review.rules'),
      value:
        on.length === 0
          ? t('integrations.review.rulesEmpty')
          : on.map((rule) => ruleLabel(t, rule)).join(', '),
    },
    {
      key: 'schedule',
      label: t('integrations.review.schedule'),
      value: `${t('integrations.overwatchAt', { at: draft.overwatchAt })} · ${t(
        'integrations.conflict',
        {
          winner: t(
            draft.conflictWinner === 'remote'
              ? 'integrations.conflict.remote'
              : 'integrations.conflict.local',
          ),
        },
      )}`,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-lg border">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex items-start gap-3 border-b px-3 py-2 text-[12.5px] last:border-0"
          >
            <span className="text-muted-foreground w-[110px] shrink-0">{row.label}</span>
            <span className="min-w-0 flex-1 leading-relaxed">{row.value}</span>
          </div>
        ))}
      </div>
      <p className="bg-primary/5 border-primary/40 rounded-lg border p-3 text-[12px] leading-relaxed">
        {t('integrations.review.note')}
      </p>
    </div>
  );
}
